/**
 * LLM Provider Mode Selector + insufficient-credit switch guard.
 * ---------------------------------------------------------------------------
 * <LlmModeSelector />        — segmented Commercial / Open-Source toggle the
 *                              end-user picks as the active LLM. Shows the
 *                              remaining commercial AI credits.
 * useLlmCreditGuard()        — returns { guardDialog, ensureSufficientCredit }.
 *                              Call ensureSufficientCredit() before any AI
 *                              action; if the active mode lacks credit it
 *                              prompts the user to switch to the free OSS mode
 *                              and resolves true (proceed) / false (abort).
 */

import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Cpu, Leaf, Coins, Loader2, Wand2, ShoppingCart } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useQueryClient } from '@tanstack/react-query'
import {
  useProviderMode,
  useSetProviderMode,
  useLlmModeStore,
  llmModeKeys,
  fetchProviderStatus,
  setProviderMode,
  type LlmMode,
  type ProviderModeStatus,
} from '@/services/llm-mode.service'

// ── Selector ──────────────────────────────────────────────────────────────

export function LlmModeSelector({ className }: { className?: string }) {
  const { data: status, isLoading } = useProviderMode()
  const setModeMut = useSetProviderMode()
  const setStoreStatus = useLlmModeStore((s) => s.setStatus)

  useEffect(() => {
    if (status) setStoreStatus(status)
  }, [status, setStoreStatus])

  const mode: LlmMode = status?.mode ?? 'commercial'
  const credits = status?.commercial_credits ?? 0
  const ossModel = status?.oss_model ?? 'qwen3-14b'
  const gpuEnabled = status?.gpu_enabled !== false
  // OSS availability now combines the GPU master switch AND the admin's explicit
  // "OSS LLM enabled" toggle (global default + per-tenant override). When false
  // the OSS option is HIDDEN entirely from the user (admin decision).
  const ossAvailable = status?.oss_available !== false

  const pick = (next: LlmMode) => {
    if (next === mode || setModeMut.isPending) return
    if (next === 'oss' && !ossAvailable) return // OSS disabled by admin
    setModeMut.mutate({ mode: next })
  }

  const segBtn = (
    value: LlmMode,
    label: string,
    Icon: typeof Cpu,
    activeClass: string,
    title: string,
    disabled = false,
  ) => (
    <button
      type="button"
      onClick={() => pick(value)}
      aria-pressed={mode === value}
      disabled={disabled}
      data-testid={`llm-mode-${value}`}
      data-disabled={disabled ? 'true' : undefined}
      className={cn(
        'flex items-center gap-1 rounded px-2 py-1 transition-colors',
        disabled && 'opacity-40 cursor-not-allowed',
        mode === value
          ? `bg-background font-medium shadow-sm ${activeClass}`
          : 'text-muted-foreground hover:text-foreground',
      )}
      title={title}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  )

  return (
    <div
      className={cn('flex items-center gap-2', className)}
      data-testid="llm-mode-selector"
      data-mode={mode}
    >
      <div className="inline-flex rounded-md border border-border bg-muted/40 p-0.5 text-xs">
        {segBtn('commercial', 'Commercial', Cpu, 'text-foreground',
          'LLM commercial (Claude / GPT) — consomme des crédits IA')}
        {segBtn('auto', 'Auto', Wand2, 'text-indigo-600',
          ossAvailable
            ? 'Auto — commercial tant qu’il y a du crédit, bascule OSS gratuit sinon'
            : 'Auto — commercial (OSS désactivé par l’administrateur : pas de bascule OSS)')}
        {/* OSS option is hidden entirely when the admin has disabled OSS LLM */}
        {ossAvailable && segBtn('oss', 'OSS', Leaf, 'text-emerald-600',
          `LLM open source (${ossModel}) — gratuit, 0 crédit`)}
      </div>
      {(isLoading || setModeMut.isPending) ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
      ) : mode === 'oss' ? (
        <span className="text-xs text-emerald-600" title={`Mode gratuit — ${ossModel}`}>gratuit</span>
      ) : (
        <span
          className={cn(
            'inline-flex items-center gap-1 text-xs',
            credits <= 0 ? 'text-destructive' : 'text-muted-foreground',
          )}
          title="Crédits IA commerciaux restants"
        >
          <Coins className="h-3.5 w-3.5" />
          {credits}
        </span>
      )}
    </div>
  )
}

// ── Pre-action credit guard ─────────────────────────────────────────────────

interface PendingPrompt {
  status: ProviderModeStatus
  resolve: (proceed: boolean) => void
}

export function useLlmCreditGuard() {
  const [pending, setPending] = useState<PendingPrompt | null>(null)
  const [switching, setSwitching] = useState(false)
  const setStoreStatus = useLlmModeStore((s) => s.setStatus)
  const qc = useQueryClient()
  const navigate = useNavigate()

  /**
   * Resolve true if the active LLM mode can run an AI action; otherwise prompt
   * the user to switch to the free OSS mode and resolve their choice.
   */
  const ensureSufficientCredit = useCallback(
    async (estimated = 5): Promise<boolean> => {
      const status = await fetchProviderStatus(estimated)
      setStoreStatus(status)
      if (status.sufficient) return true
      return new Promise<boolean>((resolve) => setPending({ status, resolve }))
    },
    [setStoreStatus],
  )

  const onSwitch = useCallback(async () => {
    if (!pending) return
    const target: LlmMode = pending.status.suggested_mode ?? 'oss'
    setSwitching(true)
    try {
      const s = await setProviderMode(target, pending.status.estimated_needed)
      setStoreStatus(s)
      // Refresh the selector's query cache so its badge flips to OSS immediately.
      qc.invalidateQueries({ queryKey: llmModeKeys.all })
    } catch {
      /* even if persistence fails, let the action proceed in OSS */
    } finally {
      setSwitching(false)
      pending.resolve(true)
      setPending(null)
    }
  }, [pending, setStoreStatus, qc])

  const onCancel = useCallback(() => {
    pending?.resolve(false)
    setPending(null)
  }, [pending])

  const onBuy = useCallback(() => {
    pending?.resolve(false)
    setPending(null)
    navigate('/self-manage/finances')
  }, [pending, navigate])

  // When action is blocked (no commercial credit AND OSS unavailable) the only
  // path is to buy credits — there is no OSS escape hatch to offer.
  const blocked = !!pending?.status.action_blocked

  const guardDialog = (
    <AlertDialog open={!!pending} onOpenChange={(o) => { if (!o) onCancel() }}>
      <AlertDialogContent data-testid={blocked ? 'llm-credit-buy-dialog' : 'llm-credit-switch-dialog'}>
        <AlertDialogHeader>
          <AlertDialogTitle>{blocked ? 'Crédits IA requis' : 'Crédit IA insuffisant'}</AlertDialogTitle>
          <AlertDialogDescription>
            {blocked ? (
              pending?.status.block_message ||
              "Vos crédits IA commerciaux sont épuisés et le mode open source (OSS) est indisponible. Achetez des crédits IA pour utiliser cette fonctionnalité."
            ) : (
              <>
                Le mode commercial (Claude / GPT) ne dispose plus d&apos;assez de crédits IA
                {' '}({pending?.status.commercial_credits ?? 0} restant
                {(pending?.status.commercial_credits ?? 0) > 1 ? 's' : ''}) pour cette action.
                Voulez-vous basculer sur le mode open source (Mistral / Llama, gratuit) afin de
                continuer&nbsp;?
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel} disabled={switching}>Annuler</AlertDialogCancel>
          {blocked ? (
            <AlertDialogAction onClick={onBuy} data-testid="ai-buy-credits">
              <ShoppingCart className="mr-1 h-4 w-4" />Acheter des crédits IA
            </AlertDialogAction>
          ) : (
            <AlertDialogAction onClick={onSwitch} disabled={switching}>
              {switching ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Leaf className="mr-1 h-4 w-4" />}
              Basculer en open source
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )

  return { guardDialog, ensureSufficientCredit }
}
